import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  AI_AGENT_MANUAL_UPLOAD_CHANNELS,
  AI_AGENT_TASK_STATUSES,
  manualUploadChannelOptions,
  taskStatusOptions,
} from '../../../../../../../shared/constants/aiAgentOptions';
import { cleanParams } from '../../../../../../../shared/utils/cleanParams';
import { useModelOptions } from '../../../Common/hooks/useModelOptions';

import type { KeyboardEvent, MouseEvent } from 'react';
import type { IGetManualsParams } from '../../../../../../../shared/apis/ai-agent/manual/types';
import type { AiAgentManualSearchType, IAiAgentManualFilterForm } from '../types';

const MODEL_FILTER_ALL = 'all';

const initialFilterForm: IAiAgentManualFilterForm = {
  types: [],
  statuses: [],
  modelId: MODEL_FILTER_ALL,
};

const initialSearchType: AiAgentManualSearchType = 'manualName';

const isAiAgentManualSearchType = (value: string): value is AiAgentManualSearchType => {
  return value === 'manualName';
};

const isAiAgentManualUploadChannel = (value: string): value is IAiAgentManualFilterForm['types'][number] => {
  return value !== AI_AGENT_MANUAL_UPLOAD_CHANNELS.ALL && manualUploadChannelOptions.some((option) => option.value === value);
};

const isAiAgentTaskStatus = (value: string): value is IAiAgentManualFilterForm['statuses'][number] => {
  return value !== AI_AGENT_TASK_STATUSES.ALL && taskStatusOptions.some((option) => option.value === value);
};

const parseFilterValues = <T extends string>(value: string | null, isValidValue: (item: string) => item is T): T[] => {
  if (!value) {
    return [];
  }

  return Array.from(
    new Set(
      value
        .split(',')
        .map((item) => item.trim())
        .filter(isValidValue),
    ),
  );
};

const parsePositiveInteger = (value: string | null, fallback: number): number => {
  const parsedValue = Number(value);
  return Number.isInteger(parsedValue) && parsedValue > 0 ? parsedValue : fallback;
};

export default function useAiAgentManualFilters(companyId: number) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [filterForm, setFilterForm] = useState<IAiAgentManualFilterForm>(initialFilterForm);
  const [searchType, setSearchType] = useState<AiAgentManualSearchType>(initialSearchType);

  const searchTypeParam = searchParams.get('searchType') || initialSearchType;
  const keywordParam = searchParams.get('keyword') || '';
  const typeParam = searchParams.get('type');
  const statusParam = searchParams.get('status');
  const modelIdParam = searchParams.get('modelId') || MODEL_FILTER_ALL;
  const modelOptions = useModelOptions(companyId);
  const parsedModelId = parsePositiveInteger(modelIdParam, 0);

  const queryParams: IGetManualsParams = {
    companyId,
    page: parsePositiveInteger(searchParams.get('page'), 1),
    size: parsePositiveInteger(searchParams.get('size'), 10),
    modelId: parsedModelId || undefined,
    fileName: keywordParam || undefined,
    taskStatuses: parseFilterValues(statusParam, isAiAgentTaskStatus).join(',') || undefined,
    regServiceChannels: parseFilterValues(typeParam, isAiAgentManualUploadChannel).join(',') || undefined,
  };

  const updateFilterForm = (nextFilter: Partial<IAiAgentManualFilterForm>) => {
    setFilterForm((prev) => ({
      ...prev,
      ...nextFilter,
    }));
  };

  const onPageChange = (page: number) => {
    const currentParams = Object.fromEntries(searchParams.entries());

    setSearchParams(
      cleanParams({
        ...currentParams,
        page: String(page),
      }),
    );
  };

  const handleSearchType = (value: string | null) => {
    if (!value || !isAiAgentManualSearchType(value)) {
      setSearchType(initialSearchType);
      return;
    }

    setSearchType(value);
  };

  const resetSearchInput = () => {
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const applyFilters = () => {
    const currentParams = Object.fromEntries(searchParams.entries());

    setSearchParams(
      cleanParams({
        ...currentParams,
        page: '1',
        searchType,
        keyword: inputRef.current?.value || '',
        type: filterForm.types.join(','),
        status: filterForm.statuses.join(','),
        modelId: filterForm.modelId === MODEL_FILTER_ALL ? '' : filterForm.modelId,
      }),
    );
  };

  const handleSearch = (event: MouseEvent<HTMLButtonElement> | KeyboardEvent<HTMLInputElement>) => {
    event.preventDefault();

    if ('key' in event && event.key !== 'Enter') {
      return;
    }

    applyFilters();
  };

  const resetFilters = () => {
    resetSearchInput();
    setSearchType(initialSearchType);
    setFilterForm(initialFilterForm);

    const currentParams = Object.fromEntries(searchParams.entries());

    setSearchParams(
      cleanParams({
        ...currentParams,
        page: '1',
        searchType: initialSearchType,
        keyword: '',
        type: '',
        status: '',
        modelId: '',
      }),
    );
  };

  useEffect(() => {
    setSearchType(isAiAgentManualSearchType(searchTypeParam) ? searchTypeParam : initialSearchType);
    setFilterForm({
      types: parseFilterValues(typeParam, isAiAgentManualUploadChannel),
      statuses: parseFilterValues(statusParam, isAiAgentTaskStatus),
      modelId: modelIdParam,
    });

    if (inputRef.current) {
      inputRef.current.value = keywordParam;
    }
  }, [keywordParam, modelIdParam, searchTypeParam, statusParam, typeParam]);

  return {
    filterForm,
    queryParams,
    modelOptions,
    searchType,
    inputRef,
    onPageChange,
    updateFilterForm,
    handleSearchType,
    handleSearch,
    resetSearchInput,
    resetFilters,
  };
}
